import { notFound, useParams } from 'next/navigation';

import React, { ChangeEvent, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';

import classNames from 'classnames';
import { ethers } from 'ethers';

import { useModalController } from '@uikit';

import { useLaunchpadProjectWithSalesQuery, useLaunchpadSaleQuery } from '@query';

import {
  KYC_STATUS,
  LAUNCHPAD_SALE_POOL_STATUS,
  LAUNCHPAD_SALE_POOL_TYPE,
  LaunchpadProject,
} from '@api';

import BaseInfoTooltip from '@shared/common/components/BaseInfoTooltip';
import { useTagManager } from '@shared/common/providers/GoogleTagManagerProvider';
import { LAUNCHPAD_TOKEN_INFO } from '@shared/constants';
import { normalizeError } from '@shared/helpers/displayErrorToast';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import { formatNumberLocalized, truncateDecimals } from '@shared/helpers/formatters';
import formatUnits from '@shared/helpers/web3/formatters/formatUnits';
import IOSSwitch from '@shared/muiKit/components/Switch/IOSSwitch';

import PaymentIcons from '@launchpad/components/common/PaymentIcons/PaymentIcons';
import { getNumberWithSymbol } from '@launchpad/components/pages/Portfolio/ui/PortfolioPool/PortfolioBasePool';
import DepositSuccessfulModal from '@launchpad/components/pages/Sale/ui/modals/DepositSuccessfulModal';
import { useAuth } from '@launchpad/components/providers/AuthProvider/AuthProvider';
import { useAuthModal } from '@launchpad/components/ui/buttons/ConnectWalletButton';
import CountryRestrictedModal from '@launchpad/components/ui/modals/CountryRestrictedModal/CountryRestrictedModal';
import KycRequiredModal from '@launchpad/components/ui/modals/KycRequiredModal/KycRequiredModal';
import useDepositTokenQuery from '@launchpad/hooks/useDepositTokenQuery';
import { useLaunchpadTotalDeposited } from '@launchpad/hooks/useLaunchpadTotalDeposited';
import useSelfDepositedAmountQuery from '@launchpad/hooks/useSelfDepositedAmountQuery';
import useTmaRestrictionModal from '@launchpad/hooks/useTmaRestrictionModal';
import useTokenInfo from '@launchpad/hooks/useTokenInfo';
import { isProRataType } from '@launchpad/utils/contract';

import useMakeDeposit from '../../hooks/useMakeDeposit';
import ConfirmBoostModal from '../modals/ConfirmBoostModal';

export interface FormData {
  amount: string;
}

const defaultValues = {
  amount: '',
};

export type DepositProps = {
  project?: LaunchpadProject;
};

export const calcProfitAmount = ({
  amount,
  tokenSalePrice,
  decimals,
}: {
  amount: number | string;
  tokenSalePrice: number | string;
  decimals: number;
}) => {
  if (!amount || amount === '.' || !tokenSalePrice) {
    return 0;
  }

  const salePriceDecimalsCount = String(tokenSalePrice).split('.')[1]?.length || 0;
  const amountBigNumber = ethers.utils.parseUnits(
    truncateDecimals(amount, decimals + salePriceDecimalsCount),
    decimals + salePriceDecimalsCount,
  );
  const salePriceBigNumber = ethers.utils.parseUnits(
    String(tokenSalePrice),
    salePriceDecimalsCount,
  );

  return amountBigNumber.div(salePriceBigNumber);
};

const MAX_ALLOWED_AMOUNT = 1_000_000_000_000;

const Deposit = () => {
  const params = useParams<{ id: string; saleId: string }>();
  const {
    handleSubmit,
    control,
    setValue,
    reset: resetForm,
    clearErrors,
    formState: { isSubmitting },
    watch,
    trigger,
  } = useForm<FormData>({
    defaultValues,
    mode: 'onBlur',
  });

  const [boosted, setBoosted] = useState(false);

  const amount = watch('amount');
  const { login } = useAuthModal();
  const kycRequiredModal = useModalController();
  const countryRestrictedModal = useModalController();
  const successfulDepositModal = useModalController();
  const boostConfirmModal = useModalController();
  const { isAuth, launchpadUser, user, refetchLaunchpadUser } = useAuth();
  const { tagManager } = useTagManager();

  const { makeDeposit } = useMakeDeposit();
  const {
    data: sale,
    refetch: refetchSale,
    isPending: isLoadingSale,
  } = useLaunchpadSaleQuery(params.saleId);
  const { data: project, isPending: isLoadingProject } = useLaunchpadProjectWithSalesQuery(
    params.id,
  );
  const { data: depositTokenInfo, isPending: isLoadingDepositToken } = useDepositTokenQuery({
    depositContractAddress: sale?.attributes.depositContractAddress,
    type: sale?.attributes.type,
  });
  const { data: userBalance, refetch: refetchUserBalance } = useTokenInfo({
    walletAddress: launchpadUser?.attributes.address,
    tokenAddress: depositTokenInfo?.address || '0x00',
    enabled: !!depositTokenInfo?.address,
  });
  const { data: selfDepositInfo, refetch: refetchSelfDepositedAmount } =
    useSelfDepositedAmountQuery({
      depositContractAddress: sale?.attributes.depositContractAddress,
      type: sale?.attributes.type,
    });
  const selfDepositedAmount = selfDepositInfo?.baseDeposited || 0;

  const { refetch: refetchTotalDeposited, data: totalDeposited = 0 } = useLaunchpadTotalDeposited({
    depositContractAddress: sale?.attributes.depositContractAddress,
    type: sale?.attributes.type,
  });

  const isProRata = isProRataType(sale?.attributes.type);

  const isSqrpSale =
    sale?.attributes.type === LAUNCHPAD_SALE_POOL_TYPE.SQRP_GATED ||
    sale?.attributes.type === LAUNCHPAD_SALE_POOL_TYPE.PRO_RATA_SQRP_GATED;

  const sqrpLock = isSqrpSale ? Number(amount) : 0;

  const minAllocation = sale?.attributes.minAllocation || 0;
  const maxAllocation = sale?.attributes.maxAllocation || 0;
  const userSqrpBalance = launchpadUser?.attributes.sqrpBalance || 0;
  const salePrice = sale?.attributes?.price || 0;
  const availableDepositToGoal = Math.max((sale?.attributes.goal || 0) - totalDeposited, 0);
  const availableAllocation = Math.max(maxAllocation - selfDepositedAmount, 0);
  const availableDepositAmount = Math.min(availableAllocation, availableDepositToGoal);
  const { isAppOpenedInTelegram, tmaRestrictionModal } = useTmaRestrictionModal();

  const profitAmount = calcProfitAmount({
    amount,
    tokenSalePrice: salePrice,
    decimals: depositTokenInfo?.decimals || LAUNCHPAD_TOKEN_INFO.decimals,
  });

  const formattedProfitAmount = formatUnits(profitAmount, depositTokenInfo?.decimals);

  const tokenPrice =
    sale?.attributes.depositTokenTicker === 'USDT'
      ? sale?.attributes?.price
      : (sale?.attributes.depositTokenPrice || 0) * (sale?.attributes?.price || 0);

  const receipt = [
    {
      id: 'token-price',
      label: 'Token Price',
      content: (
        <>
          1 {project?.attributes.ticker} = {getNumberWithSymbol(tokenPrice)}
        </>
      ),
    },
    {
      id: 'deposit',
      label: 'You deposit',
      content: `${formatNumberLocalized(Number(amount))} ${
        depositTokenInfo?.symbol || LAUNCHPAD_TOKEN_INFO.symbol
      }${sqrpLock ? ` + ${formatNumberLocalized(sqrpLock)} SQRp` : ''}`,
    },
  ];

  if (isProRata && sale?.attributes.turboChargeAvailable) {
    const buyingPowerMultiplier = selfDepositInfo?.boosted || boosted ? 5 : 1;
    const buyingPower = Number(amount) * buyingPowerMultiplier;
    receipt.push({
      id: 'buying-power',
      label: 'Your buying power',
      content: `${formatNumberLocalized(buyingPower)}`,
    });
  }

  if (!isProRata) {
    receipt.push({
      id: 'result',
      label: 'You will get',
      content: `${formatNumberLocalized(Number(formattedProfitAmount), {
        options: {
          minimumFractionDigits: 0,
          maximumFractionDigits: 8,
        },
      })} ${project?.attributes?.ticker}`,
    });
  }

  useEffect(() => {
    if (!isAuth) {
      resetForm();
    }
  }, [isAuth, resetForm]);

  useEffect(() => {
    if (selfDepositInfo && selfDepositInfo?.boosted) {
      setBoosted(selfDepositInfo?.boosted);
    }
  }, [selfDepositInfo]);

  const handleChangeCharge = (e: ChangeEvent<HTMLInputElement>) => {
    setBoosted(e.target.checked);

    if (
      e.target.checked &&
      !selfDepositInfo?.boosted &&
      !!selfDepositInfo?.baseDeposit &&
      !boostConfirmModal.isOpen
    ) {
      boostConfirmModal.open();
    }
  };

  const handleClickMax = () => {
    const balance = Number(truncateDecimals(userBalance?.userTokenFormatted || 0, 2) || 0);

    clearErrors('amount');

    if (isProRata) {
      setValue(
        'amount',
        balance <= availableAllocation
          ? truncateDecimals(balance, 0).toString()
          : truncateDecimals(availableAllocation || 0, 0).toString(),
      );
    } else if (balance >= availableDepositAmount) {
      setValue('amount', truncateDecimals(availableDepositAmount || 0, 2).toString());
    } else {
      setValue('amount', truncateDecimals(balance, 0).toString());
    }

    trigger('amount');
  };

  const handleFormSubmit = handleSubmit(async (formData) => {
    if (!isAuth) {
      login();

      return;
    }

    if (!sale) {
      return;
    }

    if (launchpadUser?.attributes.kycStatus !== KYC_STATUS.COMPLETED) {
      return kycRequiredModal.open();
    }

    try {
      const sessionAllocationsStatus = JSON.parse(
        sessionStorage.getItem('sessionAllocationsStatus') || '[]',
      );
      const amountAsNumber = Number(formData.amount || 0);
      const result = await makeDeposit(
        sale?.attributes.type,
        { amount: amountAsNumber, boosted },
        sqrpLock,
      );

      if (result) {
        sessionAllocationsStatus.push(result.status);

        sessionStorage.setItem(
          'sessionAllocationsStatus',
          JSON.stringify(sessionAllocationsStatus),
        );

        toast.success(
          `You've successfully deposited ${formatNumberLocalized(amountAsNumber)} ${
            userBalance?.symbol
          }`,
        );

        successfulDepositModal.open();
        resetForm();

        tagManager.dataLayer({
          dataLayer: {
            event: 'gtm.deposit',
            userId: user?.id,
            saleId: sale.id,
          },
        });
      }
    } catch (error) {
      if (normalizeError(error).message === 'Country restricted') {
        return countryRestrictedModal.open();
      }

      displayWalletErrorToast({ error });
      resetForm(formData);
    } finally {
      refetchUserBalance();
      refetchLaunchpadUser();
      refetchSelfDepositedAmount();
      refetchTotalDeposited();
      refetchSale();
    }
  });

  if (isLoadingSale || isLoadingProject || isLoadingDepositToken) {
    return <div className="skeleton-block min-h-[400px]" />;
  }

  if (!sale) {
    return notFound();
  }

  if (sale?.attributes.status !== LAUNCHPAD_SALE_POOL_STATUS.SALE_STARTED) {
    const isAnnounced = sale?.attributes.status === LAUNCHPAD_SALE_POOL_STATUS.ANNOUNCED;
    return (
      <div className="p-4 rounded-lg border border-primary-200 py-10 flex justify-center items-center flex-col">
        <h2 className="t-h3 pb-3">{isAnnounced ? 'Sale Announced' : 'Sale Closed'}</h2>
      </div>
    );
  }

  const isDepositButtonDisabled =
    isSubmitting || (!isProRata && selfDepositedAmount >= maxAllocation);
  const showDepositMaxButton = Number(userBalance?.userTokenFormatted || 0) > 0;
  const showSqrpField = Boolean(isSqrpSale);
  const showBoost = Boolean(sale?.attributes.turboChargeAvailable);

  return (
    <div className="p-4 rounded-lg border border-primary-200">
      <KycRequiredModal {...kycRequiredModal.props} />
      <CountryRestrictedModal {...countryRestrictedModal.props} />
      <DepositSuccessfulModal {...successfulDepositModal.props} />
      <form className="flex flex-col gap-4" onSubmit={handleFormSubmit}>
        <div className="flex justify-start sm:justify-between flex-col items-start sm:flex-row gap-2">
          <p>Deposit Amount</p>

          {showDepositMaxButton && (
            <div className="flex justify-end items-center gap-2">
              <p>
                {formatNumberLocalized(Number(userBalance?.userTokenFormatted) || 0)}{' '}
                {userBalance?.symbol}
              </p>

              <button type="button" className="text-primary" onClick={handleClickMax}>
                MAX
              </button>
            </div>
          )}
        </div>

        <Controller
          control={control}
          name="amount"
          rules={{
            validate: {
              isRequired: (value) => {
                if (!value) {
                  return 'Field is required';
                }
              },
              isGreaterThanZero: (value) => {
                if (Number(value) === 0) {
                  return `Should be greater than 0`;
                }
              },
              isGreaterThanMin: (value) => {
                if (Number(value) < minAllocation) {
                  return `Should be greater than or equal to ${minAllocation}`;
                }
              },
              isLessOrEqualToBalance: (value) => {
                if (userBalance && Number(value) > Number(userBalance.userTokenFormatted)) {
                  return `You don’t have enough tokens`;
                }
              },
              isLessOrEqualToMax: (value) => {
                if (!isProRata && totalDeposited >= sale?.attributes.goal) {
                  return 'The pool is currently full, and no further allocations are available at this time';
                }

                if (availableAllocation === 0) {
                  return `You do not have an available allocation amount`;
                }

                if (
                  Number(value) > availableAllocation ||
                  (!isProRata && Number(value) > availableDepositAmount)
                ) {
                  return `Your sum allocation should be less than or equal to ${formatNumberLocalized(
                    isProRata ? availableAllocation : availableDepositAmount,
                  )}`;
                }
              },
            },
          }}
          render={({ field: { value, ref, onChange, ...field }, fieldState }) => {
            return (
              <>
                <NumericFormat
                  getInputRef={ref}
                  className={classNames('rounded-full text-field max-md:text-base text-sm', {
                    'text-field-error': fieldState.error,
                  })}
                  id="deposit-amount"
                  allowNegative={false}
                  decimalScale={0}
                  isAllowed={(values) => {
                    if (!values.floatValue) {
                      return true;
                    }

                    return values.floatValue <= MAX_ALLOWED_AMOUNT;
                  }}
                  // NOTE: when disabled is set on Controller watch() will return undefined
                  // we don't want this behavior, because we need to show calc output values correctly during submit
                  disabled={isSubmitting}
                  thousandSeparator
                  value={value}
                  onValueChange={(values, info) => {
                    if (info.source === 'prop') {
                      return;
                    }

                    onChange(values.value);
                  }}
                  placeholder="0"
                  {...field}
                />
                {!!fieldState.error && (
                  <p className="form-control-helper-text form-control-helper-text-error">
                    {fieldState.error.message}
                  </p>
                )}
              </>
            );
          }}
        />

        {showSqrpField && (
          <>
            <div className="flex justify-start sm:justify-between flex-col items-start sm:flex-row gap-2">
              <p>Deposit Amount SQRp</p>
              {userSqrpBalance > 0 && (
                <div className="flex justify-end items-center gap-2">
                  <p>{formatNumberLocalized(userSqrpBalance)} SQRp</p>
                </div>
              )}
            </div>
            <input
              type="number"
              value={sqrpLock?.toFixed(0) || 0}
              disabled
              className="rounded-full text-field max-md:text-base text-sm"
            />
          </>
        )}

        {showBoost && (
          <div className="border rounded-lg border-green-700 bg-green-50 p-6 flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <div className="flex justify-start gap-2">
                <h4>Turbo Charge</h4>
                <BaseInfoTooltip content="Once you have used Turbo Charge for your initial deposit to this sales pool, all future deposits must also use Turbo Charge. This ensures a consistent allocation boost for all your contributions. As a result, the Turbo Charge option cannot be disabled after your first deposit." />
              </div>
              <IOSSwitch
                checked={boosted}
                onChange={handleChangeCharge}
                disabled={selfDepositInfo?.boosted}
              />
            </div>
            <p className="font-semibold text-neutral-500 text-sm">Turbo Charge Your Allocation!</p>
            <p className="text-neutral-500 text-sm">
              Boost your allocation by <span className="text-primary-700">500%</span> with Magic
              Turbo Charge. Your excessive deposit will be returned in $SQR tokens.
            </p>
          </div>
        )}

        <p className="font-semibold text-neutral-500">
          The amount you want to deposit in the project
        </p>

        <ul className="flex flex-col gap-4">
          {receipt.map(({ id, label, content }) => {
            return (
              <li key={id} className="flex justify-between items-center">
                <span className="font-semibold text-neutral-500">{label}</span>
                <span className="font-medium">{content}</span>
              </li>
            );
          })}
        </ul>

        <div className="flex justify-between items-center mt-10 font-semibold text-neutral-500">
          <span className="text-xs">Network and payment method</span>
          <div className="flex gap-2">
            <PaymentIcons tokens={[depositTokenInfo?.symbol || LAUNCHPAD_TOKEN_INFO.symbol]} />
          </div>
        </div>

        <button
          type={isAppOpenedInTelegram ? 'button' : 'submit'}
          disabled={isDepositButtonDisabled}
          className="button w-full ui-button ui-button-solid-primary"
          onClick={isAppOpenedInTelegram ? tmaRestrictionModal.open : undefined}
        >
          {sqrpLock > 0 ? 'Burn SQRp and Deposit' : 'Deposit'}
        </button>
      </form>
      <ConfirmBoostModal setBoosted={setBoosted} {...boostConfirmModal.props} />
    </div>
  );
};

export default Deposit;
